<template>
  <div>
    <unroll-element
      v-for="(metavariables_dict_poid, index) in metavariables_dicts_poids"
      :title="stringified_dict(metavariables_dict_poid)"
      :available="data_instantiated[index]"
    >
      <div v-if="data_instantiated[index]">
        <p class="h5">
          <b> {{ $t('initial_and_modified_columns_coercion') }}</b>
        </p>

        <div class="synaplus-cosmo-scroll-higher-720">
          <b-table
            striped
            hover
            :items="coercion_table_items[index]"
            :fields="coercion_table_fields"
          />
        </div>
      </div>
    </unroll-element>

    <p class="h5">
      <b> {{ $t('corrected_columns_coercion') }}</b>
    </p>
    <columns-choice-options
      v-model="columns_coercion_delta"
      :available_column_names="columns_names"
      :entry_constructor="generic_column_name_for_coercion_constructor"
    >
      <template #sub_component="props">
        <generic-column-name-edition-for-coercion
          v-bind="props"
          v-on="props.listeners"
        />
      </template>
    </columns-choice-options>

    <b-form-checkbox v-model="file_contains_headers">
      {{ $t('file_contains_headers') }}
      <span
        v-c-tooltip="$t('automatic_header_naming')"
        class="fas fa-question-circle"
      />
    </b-form-checkbox>
  </div>
</template>

<i18n>
{
  "en": {
    "initial_and_modified_columns_coercion": "Initial and modified columns' coercion",
    "corrected_columns_coercion": "Automatic coercion corrections",
    "file_contains_headers": "The file contains headers",
    "automatic_header_naming": "If unchecked, COsMO will automatically assign generated header names"
  },
  "fr": {
    "initial_and_modified_columns_coercion": "Coercion initiale et modifiée des colonnes",
    "corrected_columns_coercion": "Corrections à la coercion automatique",
    "file_contains_headers": "Le fichier contient une ligne de headers",
    "automatic_header_naming": "Si non coché, COsMO attribuera des noms de colonnes générés automatiquement"
  }
}
</i18n>
<script>
import _ from 'lodash'
import Vue from 'vue'

import { global_brick_mixin } from '@/components/mixin.js'
import { get_columnstore_listing_from_poid } from '@/api/subscriptions.js'

import UnrollElement from './unroll_element.vue'
import ColumnsChoiceOptions from './columns_choice_options.vue'
import CoercionDateFormats from './coercion_date_formats.vue'
import GenericColumnNameEditionForCoercion from './generic_column_name_edition_for_coercion.vue'
import {
  GenericColumnNameForCoercion,
  CoercionFormatCategoryToString,
  CoercionInfoDelta,
} from '@/components/object_model.js'

// *****************************************************************************

export default {
  name: 'BrickFileSubcomponentCoercionInfoDelta',
  components: {
    UnrollElement,
    ColumnsChoiceOptions,
    CoercionDateFormats,
    GenericColumnNameForCoercion,
    GenericColumnNameEditionForCoercion,
    CoercionInfoDelta,
  },
  mixins: [global_brick_mixin],
  model: {
    prop: 'coercion_info_delta',
    event: 'input',
  },
  props: ['coercion_info_delta', 'cur_brick_coercion_info_delta'],
  data() {
    return {
      headers: '',
      file_contains_headers: false,
      columns_coercion_delta: undefined,
      local_res: [],
    }
  },

  computed: {
    _() {
      return _
    },
    automatic_coercion_infos() {
      let res = _.map(
        this.tracking_infos,
        tracking_info =>
          this.fetch_poid_data(
            `automatic_coercion_info_${
              (tracking_info || {}).automatic_coercion_info_poid
            }`,
            (tracking_info || {}).automatic_coercion_info_poid
          ) || {}
      )
      return res
    },

    automatic_coercion_infos_instantiated() {
      let res = _.map(
        this.tracking_infos,
        tracking_info =>
          this.fetch_poid_status(
            `automatic_coercion_info_instantiated_${
              (tracking_info || {}).automatic_coercion_info_poid
            }`,
            (tracking_info || {}).automatic_coercion_info_poid
          ) || {}
      )

      return res
    },

    delta_corrected_coercion_infos() {
      let res = _.map(
        this.tracking_infos,
        tracking_info =>
          this.fetch_poid_data(
            `automatic_coercion_info_${
              tracking_info.delta_corrected_coercion_info_poid
            }`,
            (tracking_info || {}).delta_corrected_coercion_info_poid
          ) || {}
      )
      return res
    },

    delta_corrected_coercion_infos_instantiated() {
      let res = _.map(
        this.tracking_infos,
        tracking_info =>
          this.fetch_poid_status(
            `delta_corrected_coercion_infos_${
              (tracking_info || {}).delta_corrected_coercion_info_poid
            }`,
            (tracking_info || {}).delta_corrected_coercion_info_poid
          ) || {}
      )

      return res
    },

    data_instantiated() {
      let res = _.zip(
        this.automatic_coercion_infos_instantiated,
        this.delta_corrected_coercion_infos_instantiated
      )
      res = _.map(res, elt => _.every(elt))
      return res
    },

    local_coercion_info_delta() {
      let res = new CoercionInfoDelta()
      res.file_contains_headers = this.file_contains_headers
      res.columns_coercion_delta = this.columns_coercion_delta
      return res
    },
    generic_column_name_for_coercion_constructor() {
      return GenericColumnNameForCoercion
    },

    coercion_table_fields() {
      return [
        { key: 'column_name', label: this.$t('column_name') },
        { key: 'initial_type', label: this.$t('initial_type') },
        { key: 'final_type', label: this.$t('final_type') },
      ]
    },

    coercion_table_items() {
      let res
      let self = this

      function construct_table_item(elt, index, poid_index) {
        let column_name = elt[0]
        let initial_type = self.display_coercion_format_category(
          elt[1].category
        )

        let final_type = self.display_coercion_format_category(
          (
            ((((self.delta_corrected_coercion_infos || {})[poid_index] || {})
              .coercers || {})[index] || {})[1] || {}
          ).category
        )
        let _cellVariants = {}
        if (!_.isEqual(initial_type, final_type)) {
          _cellVariants = { initial_type: 'warning', final_type: 'warning' }
        }
        return { column_name, initial_type, final_type, _cellVariants }
      }

      res = _.map(
        this.automatic_coercion_infos,
        (automatic_coercion_info, poid_index) =>
          _.map(automatic_coercion_info.coercers, (elt, index) =>
            construct_table_item(elt, index, poid_index)
          )
      )

      return res
    },

    columns_names() {
      let res = _.map(this.automatic_coercion_infos, info =>
        _.map(info.coercers, elt => (elt || {})[0])
      )

      res = new Set(_.flatten(res))
      res = Array.from(res)

      return res
    },
  },

  watch: {
    coercion_info_delta: {
      immediate: true,
      handler(nv, ov) {
        if (!_.isEqual(nv, ov)) {
          this.file_contains_headers = nv.file_contains_headers
          this.columns_coercion_delta = nv.columns_coercion_delta
        }
      },
    },
    local_coercion_info_delta: {
      immediate: true,
      handler(nv, ov) {
        if (!_.isEqual(nv, ov)) {
          this.$emit('input', nv)
        }
      },
    },
  },

  methods: {
    display_coercion_format_category(category) {
      let not_formated = CoercionFormatCategoryToString[category]
      let to_formated = {
        numeric_dtype: this.$t('number'),
        str_limiter: this.$t('text'),
        datetimes: this.$t('date'),
      }
      return to_formated[not_formated]
    },
  },
}
</script>
